import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PdfViewerProps {
  url: string;
}

export default function PdfViewer({ url }: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const renderVersionRef = useRef(0);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.5);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const loadingTask = pdfjsLib.getDocument(url);
    let destroyed = false;

    loadingTask.promise
      .then((pdf) => {
        if (destroyed) return;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setPageNum(1);
        setLoading(false);
      })
      .catch((err) => {
        if (destroyed) return;
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
        setLoading(false);
      });

    return () => {
      destroyed = true;
      loadingTask.destroy();
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [url]);

  // 自适应缩放：根据容器宽度调整
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      if (container && pdfDoc) {
        // 留一点边距，按宽度 90% 计算缩放
        const w = container.clientWidth * 0.9;
        // 获取第一页来估算缩放比例（A4 宽度约 595pt）
        pdfDoc.getPage(1).then((page) => {
          const vw = page.getViewport({ scale: 1 }).width;
          setScale(w / vw);
        }).catch(() => {});
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [pdfDoc]);

  const renderPage = useCallback(
    (num: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !pdfDoc) return;

      // 取消上一次渲染，防止竞态导致画面错乱
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      const version = ++renderVersionRef.current;

      pdfDoc.getPage(num).then((page) => {
        // 版本号不匹配，说明已有新的渲染请求，放弃本次
        if (version !== renderVersionRef.current) return;

        const viewport = page.getViewport({ scale });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        task.promise.finally(() => {
          if (renderTaskRef.current === task) {
            renderTaskRef.current = null;
          }
        });
      }).catch(() => {});
    },
    [pdfDoc, scale],
  );

  useEffect(() => {
    renderPage(pageNum);
  }, [renderPage, pageNum]);

  const prevPage = () => setPageNum((n) => Math.max(1, n - 1));
  const nextPage = () => setPageNum((n) => Math.min(numPages, n + 1));

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 bg-gray-50">
        <div className="w-10 h-10 border-3 border-[#0069BD] border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-500">Loading PDF...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 bg-gray-50">
        <i className="ri-error-warning-line text-4xl text-amber-500" />
        <span className="text-sm text-gray-600">{error}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* 页面导航栏 */}
      <div className="flex items-center justify-center gap-3 px-4 py-2 bg-white/90 backdrop-blur shrink-0 border-b border-gray-100">
        <button
          onClick={prevPage}
          disabled={pageNum <= 1}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <span className="text-sm text-gray-700 font-medium tabular-nums">
          {pageNum} / {numPages}
        </span>
        <button
          onClick={nextPage}
          disabled={pageNum >= numPages}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      {/* Canvas 区域 */}
      <div ref={containerRef} className="flex-1 overflow-auto flex justify-center p-2">
        <canvas ref={canvasRef} className="shadow-lg" />
      </div>
    </div>
  );
}
